'use client';

/**
 * CookieConsentBanner — H3 (Audit) — EU ePrivacy / DSGVO bottom banner.
 *
 * Renders on every page until the visitor has chosen Accept / Reject /
 * Customize. Once a choice exists in the `zenopus_cc_v1` cookie the
 * banner unmounts itself.
 *
 * Mounted in `app/[locale]/layout.tsx`; non-blocking (visitor can still
 * scroll) per ePrivacy guidance — but visible enough that the choice is
 * informed (the banner has its own background and shadow).
 *
 * The granular dialog reuses `<Dialog>` + `<ZenModalShell>` instead of
 * a hand-rolled overlay because that pair is already keyboard- +
 * focus-trap-tested and inherits the dark-mode tokens.
 */

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Cookie, ShieldCheck } from 'lucide-react';
import { Button, Label, Switch, VisuallyHidden, ZenModalShell, ZenSquareBadge } from '@/components/ui';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from '@/components/ui/dialog';
import { useCookieConsent, type CookieConsentChoices } from '@/hooks/useCookieConsent';

type CategoryKey = 'necessary' | 'analytics' | 'marketing' | 'performance';

const TOGGLE_CATEGORIES: ReadonlyArray<{ key: CategoryKey; locked: boolean }> = [
  { key: 'necessary', locked: true },
  { key: 'analytics', locked: false },
  { key: 'marketing', locked: false },
  { key: 'performance', locked: false },
];

export function CookieConsentBanner() {
  const t = useTranslations('legal.cookieBanner');
  const { choices, accept, reject, customize, hydrated } = useCookieConsent();
  const [customOpen, setCustomOpen] = useState(false);
  const [pending, setPending] = useState<Omit<CookieConsentChoices, 'necessary'>>({
    analytics: false,
    marketing: false,
    performance: false,
  });

  // Hydration gate: the banner is purely client-side. Returning `null`
  // during SSR keeps the markup deterministic so the React reconciler
  // does not flag a hydration mismatch on first paint.
  if (!hydrated) return null;

  // The visitor has already chosen — banner stays out of the way until
  // they explicitly come back to /settings/privacy to revoke.
  if (choices !== null) return null;

  const openCustom = () => {
    setPending({ analytics: false, marketing: false, performance: false });
    setCustomOpen(true);
  };

  const submitCustom = () => {
    customize(pending);
    setCustomOpen(false);
  };

  return (
    <>
      <div
        role="region"
        aria-label={t('regionLabel')}
        className="zen-layer-banner fixed inset-x-0 bottom-0 z-50 border-t border-zen-border bg-zen-surface/95 px-4 py-4 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.18)] backdrop-blur supports-[backdrop-filter]:bg-zen-surface/80 dark:bg-zen-surface/95"
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <ZenSquareBadge className="mt-0.5">
              <Cookie className="size-4" aria-hidden />
            </ZenSquareBadge>
            <div className="min-w-0">
              <p className="text-sm font-medium text-zen-text">{t('title')}</p>
              <p className="mt-1 text-xs leading-5 text-zen-text-muted">{t('body')}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:justify-end">
            <Button type="button" variant="ghost" size="sm" onClick={reject}>
              {t('rejectAll')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={openCustom}>
              {t('customize')}
            </Button>
            <Button type="button" variant="primary" size="sm" onClick={accept}>
              {t('acceptAll')}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="max-w-md p-0" hideClose>
          <ZenModalShell
            badge={
              <ZenSquareBadge>
                <ShieldCheck className="size-4" aria-hidden />
              </ZenSquareBadge>
            }
            title={t('customize_title')}
            subtitle={t('customize_body')}
            onClose={() => setCustomOpen(false)}
            closeLabel={t('close')}
          >
            {/* Hidden semantic DialogTitle/Description so Base UI's aria
                wiring stays correct — the visible text above is rendered
                by ZenModalShell's plain heading. Same pattern as AuthModal. */}
            <VisuallyHidden>
              <DialogTitle>{t('customize_title')}</DialogTitle>
              <DialogDescription>{t('customize_body')}</DialogDescription>
            </VisuallyHidden>
            <div className="space-y-3">
              {TOGGLE_CATEGORIES.map(({ key, locked }) => {
                const checked = locked ? true : pending[key as 'analytics' | 'marketing' | 'performance'];
                return (
                  <div
                    key={key}
                    className="flex items-start justify-between gap-3 rounded-control border border-zen-border bg-zen-muted/30 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <Label htmlFor={`zenopus-cc-${key}`} className="text-xs font-medium text-zen-text">
                        {t(`category_${key}_label` as 'category_necessary_label')}
                      </Label>
                      <p className="mt-0.5 text-[11px] leading-4 text-zen-text-muted">
                        {t(`category_${key}_body` as 'category_necessary_body')}
                      </p>
                    </div>
                    <Switch
                      id={`zenopus-cc-${key}`}
                      checked={checked}
                      disabled={locked}
                      onCheckedChange={(value: boolean) => {
                        if (locked) return;
                        setPending((prev) => ({ ...prev, [key]: value }));
                      }}
                      aria-label={t(`category_${key}_label` as 'category_necessary_label')}
                    />
                  </div>
                );
              })}
            </div>

            <DialogFooter className="mt-4">
              <Button type="button" variant="ghost" size="sm" onClick={() => setCustomOpen(false)}>
                {t('cancel')}
              </Button>
              <Button type="button" variant="primary" size="sm" onClick={submitCustom}>
                {t('savePreferences')}
              </Button>
            </DialogFooter>
          </ZenModalShell>
        </DialogContent>
      </Dialog>
    </>
  );
}
